import isNil from 'lodash/isNil'

import { defineAll } from '@app/utils/defineAll'

import type { ApiSavedAnnouncementSearch } from '@app/packages/api/v2/types/entities'
import type { SavedAnnouncementSearchPostRequest, SavedAnnouncementSearchPutRequest } from '@app/packages/api/v2/types/params'
import { unwrapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { profileSitterAddressSelector } from '@app/store/selectors/profile'
import { defaultSearchAddressResultSelector } from '@app/store/selectors/search'
import { createThunk } from '@app/store/thunk'

import type { SavedAnnouncementsFilter, SavedAnnouncementsFilterAddress } from './SavedAnnouncementsFilter'

export function getDefaultFilter() {
  return createThunk((dispatch, getState): SavedAnnouncementsFilter => {
    const defaultAddress =
      profileSitterAddressSelector(getState()) ?? dispatch(unwrapThunkSelector(defaultSearchAddressResultSelector, 'Default search address is not loaded yet'))

    return {
      created_by: 'except_ignored',
      address: { label: defaultAddress.label, coords: defaultAddress.location, radius: 1000 },
    }
  })
}

export function getEffectiveFilter(filter: SavedAnnouncementsFilter) {
  return createThunk(dispatch => {
    return { ...dispatch(getDefaultFilter()), ...filter }
  })
}

export function convertFilterToPutRequest({ name, filter }: { name?: string; filter: SavedAnnouncementsFilter }) {
  return createThunk((dispatch): SavedAnnouncementSearchPutRequest => {
    filter = dispatch(getEffectiveFilter(filter))

    return defineAll<SavedAnnouncementSearchPutRequest>({
      name,
      online: filter.online ?? null,
      min_date: filter.min_date ?? null,
      max_date: filter.max_date ?? null,
      min_hours: filter.min_hours ?? null,
      max_hours: filter.max_hours ?? null,
      start: filter.start ?? null,
      end: filter.end ?? null,
      min_kids: filter.min_kids ?? null,
      max_kids: filter.max_kids ?? null,
      youngest_age_months: filter.youngest_age_months ?? null,
      eldest_age_months: filter.eldest_age_months ?? null,
      regular: filter.regular ?? null,

      address: filter.address?.label ?? null,
      latitude: !filter.online && filter.address ? filter.address.coords.lat : null,
      longitude: !filter.online && filter.address ? filter.address.coords.lon : null,
      distance: filter.address ? filter.address.radius / 1000 : null,

      created_by: filter.created_by,
    })
  })
}

export function convertFilterToPostRequest({ name, filter }: { name: string; filter: SavedAnnouncementsFilter }) {
  return createThunk((dispatch): SavedAnnouncementSearchPostRequest => {
    return dispatch(convertFilterToPutRequest({ name, filter })) as SavedAnnouncementSearchPostRequest
  })
}

export function convertSavedSearchToFilter(savedSearch: ApiSavedAnnouncementSearch) {
  return createThunk((dispatch): SavedAnnouncementsFilter => {
    const address: SavedAnnouncementsFilterAddress | undefined = (() => {
      if (isNil(savedSearch.attributes.latitude) || isNil(savedSearch.attributes.longitude) || isNil(savedSearch.attributes.distance)) return undefined
      const address: SavedAnnouncementsFilterAddress = {
        coords: { lat: savedSearch.attributes.latitude, lon: savedSearch.attributes.longitude },
        radius: savedSearch.attributes.distance * 1000,
        label: savedSearch.attributes.address ?? undefined,
      }
      const defaultFilter = dispatch(getDefaultFilter())
      if (addressIsEqual(address, defaultFilter.address)) return undefined
      return address
    })()

    return defineAll<SavedAnnouncementsFilter>({
      online: savedSearch.attributes.online ?? undefined,
      min_date: savedSearch.attributes.min_date ?? undefined,
      max_date: savedSearch.attributes.max_date ?? undefined,
      min_hours: savedSearch.attributes.min_hours ?? undefined,
      max_hours: savedSearch.attributes.max_hours ?? undefined,
      start: savedSearch.attributes.start ?? undefined,
      end: savedSearch.attributes.end ?? undefined,
      min_kids: savedSearch.attributes.min_kids ?? undefined,
      max_kids: savedSearch.attributes.max_kids ?? undefined,
      youngest_age_months: savedSearch.attributes.youngest_age_months ?? undefined,
      eldest_age_months: savedSearch.attributes.eldest_age_months ?? undefined,
      regular: savedSearch.attributes.regular ?? undefined,

      address,

      created_by: savedSearch.attributes.created_by ?? undefined,
    })
  })
}

function addressIsEqual(a?: SavedAnnouncementsFilterAddress, b?: SavedAnnouncementsFilterAddress) {
  if (a && b) return a.coords.lat === b.coords.lat && a.coords.lon === b.coords.lon && a.radius === b.radius
  return a === b
}
